/**
 * Where the "turn these emails off" link in a digest lands:
 * /unsubscribe?token=...
 *
 * Turns the digest off on arrival, with no button and no sign-in. Clicking the
 * link was the decision; a confirmation page is one more chance to give up and
 * press "report spam" instead, which costs every clinic's deliverability rather
 * than one person's weekly email. The cost of acting on arrival is that a mail
 * scanner which executes pages could unsubscribe someone — so the page offers
 * the way back in one click, and the same setting lives under Account.
 *
 * The token is deliberately left in the address bar, unlike the reset and
 * verify screens: it does not expire and is not spent, so a refresh or a
 * bookmark still works, which is what an unsubscribe link should do.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, unsubscribeFromDigest } from '../lib/api.js';
import { c, primaryButton } from '../theme.js';
import { AuthLayout, ErrorBox, NoticeBox } from './AuthLayout.js';

type Status = 'working' | 'off' | 'on' | 'failed';

interface Props {
  token: string;
  /** Clears the path and continues into the app (or its sign-in screen). */
  onContinue: () => void;
}

export function UnsubscribeScreen({ token, onContinue }: Props) {
  const [status, setStatus] = useState<Status>('working');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const attempted = useRef(false);

  function fail(err: unknown) {
    setError(err instanceof ApiError ? err.message : 'We couldn’t reach the server. Try again.');
    setStatus('failed');
  }

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    unsubscribeFromDigest(token)
      .then(() => setStatus('off'))
      .catch(fail);
  }, [token]);

  async function turnBackOn() {
    setBusy(true);
    try {
      await unsubscribeFromDigest(token, true);
      setStatus('on');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const linkButton = {
    background: 'none',
    border: 'none',
    padding: 0,
    color: c.green,
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textAlign: 'left' as const,
    textDecoration: 'underline',
  };

  if (status === 'working') {
    return (
      <AuthLayout heading="Turning stock emails off…">
        <p style={{ fontSize: 13.5, color: c.muted, margin: 0 }}>One moment.</p>
      </AuthLayout>
    );
  }

  if (status === 'failed') {
    return (
      <AuthLayout heading="That link didn’t work">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <ErrorBox>{error}</ErrorBox>
          <p style={{ fontSize: 13, color: c.muted, margin: 0, lineHeight: 1.6 }}>
            You can change how often we email you, or stop it, by signing in and opening Account.
          </p>
          <button type="button" onClick={onContinue} style={primaryButton}>
            Go to The Good Stock
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'on') {
    return (
      <AuthLayout heading="Stock emails are back on">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <NoticeBox>
            You’ll get one every Monday when something has expired, is about to, or needs
            reordering. You can switch to daily, or off, under Account.
          </NoticeBox>
          <button type="button" onClick={onContinue} style={primaryButton}>
            Go to The Good Stock
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout heading="Stock emails are off">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <NoticeBox>
          We won’t email you about expiring or low stock again. This only changes your own emails —
          colleagues who get them still will, and the dashboard still shows what is expiring.
        </NoticeBox>
        <button type="button" onClick={onContinue} style={primaryButton}>
          Go to The Good Stock
        </button>
        <button type="button" onClick={() => void turnBackOn()} disabled={busy} style={linkButton}>
          {busy ? 'Turning back on…' : 'That was a mistake, turn them back on'}
        </button>
      </div>
    </AuthLayout>
  );
}
